import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { 
  Mail, 
  Upload, 
  Send, 
  Trash2, 
  CheckSquare, 
  Square,
  Eye,
  FileText,
  Users,
  Plus,
  X,
  Database,
  AlertCircle,
  Check
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';

interface EmailRecipient {
  email: string;
  name?: string;
  selected: boolean;
}

interface CustomField {
  id: string;
  fieldName: string;
  placeholder: string;
  values: string[];
}

export default function SendEmailTab() {
  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [htmlTemplate, setHtmlTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [previewEmail, setPreviewEmail] = useState('');
  const [previewRecipient, setPreviewRecipient] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Custom fields management
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
  const [newFieldValues, setNewFieldValues] = useState('');

  // Import emails from pasted text
  const handleImportEmails = () => {
    const lines = emailInput.split('\n').filter(line => line.trim());
    const newRecipients: EmailRecipient[] = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();
      const parts = trimmedLine.split('|').map(p => p.trim());
      
      if (parts.length === 1) {
        const email = parts[0];
        if (isValidEmail(email)) {
          newRecipients.push({
            email,
            selected: true
          });
        }
      } else if (parts.length >= 2) {
        const [name, email] = parts;
        if (isValidEmail(email)) {
          newRecipients.push({
            name,
            email,
            selected: true
          });
        }
      }
    });

    if (newRecipients.length > 0) {
      setRecipients([...recipients, ...newRecipients]);
      setEmailInput('');
      toast.success(`Imported ${newRecipients.length} email(s)`);
    } else {
      toast.error('No valid emails found');
    }
  };

  // Add custom field with values
  const handleAddCustomField = () => {
    if (!newFieldName.trim() || !newFieldPlaceholder.trim()) {
      toast.error('Please enter field name and placeholder');
      return;
    }

    if (!newFieldValues.trim()) {
      toast.error('Please add at least one value');
      return;
    }

    const values = newFieldValues.split('\n').filter(v => v.trim()).map(v => v.trim());
    
    if (values.length === 0) {
      toast.error('Please add at least one value');
      return;
    }

    const fieldId = `field_${Date.now()}`;
    const newField: CustomField = {
      id: fieldId,
      fieldName: newFieldName,
      placeholder: newFieldPlaceholder,
      values: values
    };

    setCustomFields([...customFields, newField]);
    setNewFieldName('');
    setNewFieldPlaceholder('');
    setNewFieldValues('');
    setShowAddField(false);
    toast.success(`Custom field added with ${values.length} value(s)`);
  };

  // Delete custom field
  const handleDeleteField = (fieldId: string) => {
    setCustomFields(customFields.filter(f => f.id !== fieldId));
    toast.success('Field deleted');
  };

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Toggle individual recipient selection
  const toggleRecipient = (index: number) => {
    const updated = [...recipients];
    updated[index].selected = !updated[index].selected;
    setRecipients(updated);
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    const allSelected = recipients.every(r => r.selected);
    const updated = recipients.map(r => ({ ...r, selected: !allSelected }));
    setRecipients(updated);
  };

  // Remove recipient
  const removeRecipient = (index: number) => {
    const updated = recipients.filter((_, i) => i !== index);
    setRecipients(updated);
  };

  // Clear all recipients
  const clearAllRecipients = () => {
    setRecipients([]);
    toast.success('All recipients cleared');
  };

  // Replace placeholders in HTML template
  const replacePlaceholders = (
    template: string, 
    recipient: EmailRecipient, 
    customValues: Record<string, string>
  ): string => {
    let processed = template
      .replace(/\{\{name\}\}/g, recipient.name || 'User')
      .replace(/\{\{email\}\}/g, recipient.email);

    // Replace custom field placeholders
    Object.entries(customValues).forEach(([placeholder, value]) => {
      const regex = new RegExp(`\\{\\{${placeholder}\\}\\}`, 'g');
      processed = processed.replace(regex, value);
    });

    return processed;
  };

  // Preview email for first recipient
  const handlePreview = () => {
    const selectedRecipients = recipients.filter(r => r.selected);
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    const recipient = selectedRecipients[0];
    const customValues: Record<string, string> = {};

    // Assign first available value from each custom field for preview
    customFields.forEach(field => {
      if (field.values.length > 0) {
        customValues[field.placeholder] = field.values[0];
      }
    });

    const processedHtml = replacePlaceholders(htmlTemplate, recipient, customValues);
    setPreviewEmail(processedHtml);
    setPreviewRecipient(recipient.email);
    setShowPreview(true);
  };

  // Send emails
  const handleSendEmails = async () => {
    const selectedRecipients = recipients.filter(r => r.selected);

    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    if (!htmlTemplate.trim()) {
      toast.error('Please enter an email template');
      return;
    }

    // Check if we have enough values for custom fields
    for (const field of customFields) {
      if (field.values.length < selectedRecipients.length) {
        toast.error(`Not enough values for field "${field.fieldName}". Need ${selectedRecipients.length}, have ${field.values.length}`);
        return;
      }
    }

    setIsSending(true);

    try {
      const emailData: Array<{
        to: string;
        subject: string;
        html: string;
        assignedValues: Record<string, string>;
      }> = [];

      // Prepare emails with unique values for each recipient
      selectedRecipients.forEach((recipient, index) => {
        const customValues: Record<string, string> = {};

        // Assign unique value from each custom field
        customFields.forEach(field => {
          customValues[field.placeholder] = field.values[index];
        });

        emailData.push({
          to: recipient.email,
          subject: emailSubject,
          html: replacePlaceholders(htmlTemplate, recipient, customValues),
          assignedValues: customValues
        });
      });

      // TODO: Replace with actual API call to send emails
      console.log('Sending emails:', emailData);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Remove used values from custom fields
      const updatedFields = customFields.map(field => ({
        ...field,
        values: field.values.slice(selectedRecipients.length)
      }));

      setCustomFields(updatedFields);

      // Remove sent recipients
      setRecipients(recipients.filter(r => !r.selected));

      toast.success(`Successfully sent ${selectedRecipients.length} email(s)`, {
        description: customFields.length > 0 ? 'Used values have been removed from custom fields' : undefined
      });
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send emails');
    } finally {
      setIsSending(false);
    }
  };

  const selectedCount = recipients.filter(r => r.selected).length;
  const allSelected = recipients.length > 0 && recipients.every(r => r.selected);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2>Send Email Campaign</h2>
        <p className="text-muted-foreground">
          Send emails with unique dynamic values to multiple recipients
        </p>
      </div>

      <Tabs defaultValue="recipients" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="recipients">
            <Users className="w-4 h-4 mr-2" />
            Recipients ({recipients.length})
          </TabsTrigger>
          <TabsTrigger value="fields">
            <Database className="w-4 h-4 mr-2" />
            Dynamic Fields ({customFields.length})
          </TabsTrigger>
          <TabsTrigger value="template">
            <FileText className="w-4 h-4 mr-2" />
            Email Template
          </TabsTrigger>
          <TabsTrigger value="send">
            <Send className="w-4 h-4 mr-2" />
            Review & Send
          </TabsTrigger>
        </TabsList>

        {/* Recipients Tab */}
        <TabsContent value="recipients" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Import Email Recipients</CardTitle>
              <CardDescription>
                Paste email addresses (one per line). Format: email or name|email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-input">Email List</Label>
                <Textarea
                  id="email-input"
                  placeholder={'user@example.com\nJohn Doe|john@example.com\nJane Smith|jane@example.com'}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  rows={8}
                />
              </div>
              <Button onClick={handleImportEmails} disabled={!emailInput.trim()}>
                <Upload className="w-4 h-4 mr-2" />
                Import Emails
              </Button>
            </CardContent>
          </Card>

          {recipients.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Email Recipients</CardTitle>
                    <CardDescription>
                      {selectedCount} of {recipients.length} selected
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleSelectAll}
                    >
                      {allSelected ? (
                        <>
                          <Square className="w-4 h-4 mr-2" />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Select All
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={clearAllRecipients}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[450px] rounded-md border">
                  <div className="p-4 space-y-2">
                    {recipients.map((recipient, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            checked={recipient.selected}
                            onCheckedChange={() => toggleRecipient(index)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{recipient.email}</span>
                            </div>
                            {recipient.name && (
                              <Badge variant="secondary" className="text-xs mt-1">
                                {recipient.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRecipient(index)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Dynamic Fields Tab */}
        <TabsContent value="fields" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Dynamic Fields</CardTitle>
                  <CardDescription>
                    Add fields with unique values (like coupon codes, referral codes). Each recipient gets one unique value.
                  </CardDescription>
                </div>
                {!showAddField && (
                  <Button onClick={() => setShowAddField(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Field
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {showAddField && (
                <div className="p-4 rounded-lg border bg-muted/50 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="field-name">Field Name</Label>
                      <Input
                        id="field-name"
                        placeholder="e.g., Coupon Code"
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="field-placeholder">
                        Placeholder <span className="text-muted-foreground text-xs">(use in template)</span>
                      </Label>
                      <Input
                        id="field-placeholder"
                        placeholder="e.g., couponCode"
                        value={newFieldPlaceholder}
                        onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="field-values">
                      Values <span className="text-muted-foreground text-xs">(one per line)</span>
                    </Label>
                    <Textarea
                      id="field-values"
                      placeholder={'save20\nsave30\nSAVEmore\nDISCOUNT50'}
                      value={newFieldValues}
                      onChange={(e) => setNewFieldValues(e.target.value)}
                      rows={8}
                    />
                    <p className="text-xs text-muted-foreground">
                      {newFieldValues.split('\n').filter(v => v.trim()).length} value(s) entered
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddCustomField}>
                      <Check className="w-4 h-4 mr-2" />
                      Add Field
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddField(false);
                        setNewFieldName('');
                        setNewFieldPlaceholder('');
                        setNewFieldValues('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {customFields.length === 0 && !showAddField && (
                <Alert>
                  <AlertDescription>
                    No dynamic fields added. You can send simple emails without dynamic fields, or add fields to send unique values to each recipient.
                  </AlertDescription>
                </Alert>
              )}

              {customFields.length > 0 && (
                <div className="space-y-3">
                  {customFields.map(field => (
                    <div key={field.id} className="p-4 rounded-lg border bg-card">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4>{field.fieldName}</h4>
                            <Badge variant="outline" className="font-mono text-xs">
                              {'{'}{'{'}{'}'}
                              {field.placeholder}
                              {'}'}{'}'}{'}'}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {field.values.length} value(s) available
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteField(field.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>

                      <ScrollArea className="h-40 rounded-md border p-3">
                        <div className="space-y-1">
                          {field.values.slice(0, 50).map((value, idx) => (
                            <div key={idx} className="text-xs font-mono bg-muted px-2 py-1 rounded">
                              {idx + 1}. {value}
                            </div>
                          ))}
                          {field.values.length > 50 && (
                            <div className="text-xs text-muted-foreground px-2 py-1">
                              ... and {field.values.length - 50} more values
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Template Tab */}
        <TabsContent value="template" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Template</CardTitle>
              <CardDescription>
                Create your HTML email template. Use placeholders to insert dynamic values.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  placeholder="Enter email subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="html-template">HTML Template</Label>
                <Textarea
                  id="html-template"
                  placeholder={`<html>
  <body style="font-family: Arial, sans-serif; padding: 20px;">
    <h1>Hello {{'{{name}}'}}!</h1>
    <p>Your email: {{'{{email}}'}}</p>
    ${customFields.length > 0 ? customFields.map(f => `<p>Your ${f.fieldName}: <strong>{{'{{' + f.placeholder + '}}'}} </strong></p>`).join('\n    ') : ''}
    <p>Thank you!</p>
  </body>
</html>`}
                  value={htmlTemplate}
                  onChange={(e) => setHtmlTemplate(e.target.value)}
                  rows={16}
                  className="font-mono text-sm"
                />
              </div>

              <Alert>
                <AlertDescription>
                  <strong>Available Placeholders:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>
                      <code className="bg-muted px-1 rounded text-xs">{'{'}{'{'}{'}'}name{'}'}{'}'}{'}'}</code> - Recipient name (defaults to &quot;User&quot;)
                    </li>
                    <li>
                      <code className="bg-muted px-1 rounded text-xs">{'{'}{'{'}{'}'}email{'}'}{'}'}{'}'}</code> - Recipient email address
                    </li>
                    {customFields.map(field => (
                      <li key={field.id}>
                        <code className="bg-muted px-1 rounded text-xs">{'{'}{'{'}{'}'}
                          {field.placeholder}
                          {'}'}{'}'}{'}'}</code> - {field.fieldName} (unique per recipient)
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>

              <Button onClick={handlePreview} disabled={!htmlTemplate.trim() || selectedCount === 0}>
                <Eye className="w-4 h-4 mr-2" />
                Preview Email
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Send Tab */}
        <TabsContent value="send" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Review & Send Campaign</CardTitle>
              <CardDescription>
                Review your campaign details before sending
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border bg-card">
                  <div className="text-sm text-muted-foreground mb-1">Total Recipients</div>
                  <div className="text-3xl">{recipients.length}</div>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <div className="text-sm text-muted-foreground mb-1">Selected to Send</div>
                  <div className="text-3xl">{selectedCount}</div>
                </div>
              </div>

              {emailSubject && (
                <div className="p-4 rounded-lg border bg-card">
                  <div className="text-sm text-muted-foreground mb-2">Email Subject</div>
                  <div>{emailSubject}</div>
                </div>
              )}

              {customFields.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm">Dynamic Fields Status</h4>
                  <div className="space-y-2">
                    {customFields.map(field => {
                      const availableCount = field.values.length;
                      const hasEnough = availableCount >= selectedCount;
                      return (
                        <div key={field.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{field.fieldName}</span>
                            <Badge variant="outline" className="font-mono text-xs">
                              {'{'}{'{'}{'}'}
                              {field.placeholder}
                              {'}'}{'}'}{'}'}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            {hasEnough ? (
                              <Badge variant="default" className="text-xs">
                                {availableCount} values available
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="text-xs">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Need {selectedCount}, have {availableCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Alert>
                    <AlertDescription className="text-xs">
                      <strong>Note:</strong> After sending, used values will be automatically removed from the fields.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {selectedCount > 0 && htmlTemplate && emailSubject ? (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button size="lg" className="w-full" disabled={isSending}>
                      <Send className="w-4 h-4 mr-2" />
                      {isSending ? 'Sending Emails...' : `Send ${selectedCount} Email(s)`}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Confirm Email Send</DialogTitle>
                      <DialogDescription>
                        You are about to send {selectedCount} email(s).
                        {customFields.length > 0 && (
                          <>
                            <br /><br />
                            Each recipient will receive a unique value from your dynamic fields. Used values will be automatically removed.
                          </>
                        )}
                        <br /><br />
                        This action cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => {}}>
                        Cancel
                      </Button>
                      <Button onClick={handleSendEmails} disabled={isSending}>
                        {isSending ? 'Sending...' : 'Confirm & Send'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              ) : (
                <Alert>
                  <AlertDescription>
                    <strong>Complete these steps to send emails:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                      {recipients.length === 0 && <li>Import email recipients</li>}
                      {selectedCount === 0 && recipients.length > 0 && <li>Select at least one recipient</li>}
                      {!emailSubject && <li>Enter email subject</li>}
                      {!htmlTemplate && <li>Create email template</li>}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Preview for: {previewRecipient}
              {customFields.length > 0 && (
                <span className="block mt-1 text-xs">
                  (Showing first available values from dynamic fields)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[600px] rounded-md border">
            <div 
              className="p-6"
              dangerouslySetInnerHTML={{ __html: previewEmail }}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}